import type { ContractAiAnalysisSummary } from "@closer/shared";
import {
  CONTRACT_AI_ANALYSIS_ERROR_MESSAGES,
  CONTRACT_AI_ANALYSIS_STATUS_NAMES,
} from "@closer/shared";
import { Badge, Button, Card, Skeleton, useToast } from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { AiAdvisoryNotice, AiSourceReference } from "../../components/AiOutputNotice";
import { StateView } from "../../components/StateView";
import { api } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { formatDate } from "../../lib/format";
import { usePreferences } from "../../lib/preferences";
import { tenantKey } from "../../lib/tenantQuery";
import { resolveErrorState } from "../../lib/uiState";
import { useIdempotencyKey } from "../../lib/useIdempotencyKey";

/**
 * SÖZLEŞME AI ANALİZİ (KARAR 07 · KARAR 11 · KARAR 12).
 *
 * ### ⚠ "AI ÖNERİSİDİR" ETİKETİ ZORUNLU ve METNİ ŞEMADAN
 *
 * KARAR 11: *"Her AI çıktısı 'AI önerisidir' etiketiyle sunulur; kendiliğinden
 * versiyon/karar doğurmaz."* Etiketi burada yazsaydık, ikinci dilimde (taslak sihirbazı)
 * ikinci bir yüzey açıldığında orada unutulabilir ya da başka türlü yazılabilirdi — ve
 * sorumluluğun insanda olduğunu söyleyen cümle bu ürünün en pahalı cümlesi. Tek kaynak
 * `@closer/shared` (CLAUDE.md kriter 4: *"UI string uydurmaz"*).
 *
 * ### ⚠ BU BÖLÜM HİÇBİR VERSİYON ya da TARİH DOĞURMUYOR
 *
 * Çıktıdaki kritik tarihler `contract_dates`'e yazılmıyor, özet bir versiyona
 * dönüşmüyor. KARAR 11 bunu açıkça yasaklıyor ve kullanıcının kabulüyle doğacak kayıt
 * ikinci dilimin konusu. "Tarihi ekle" düğmesi koysaydık kartsız bir yüzey açardık.
 *
 * ### ⚠ DÜŞEN KREDİ HER SONUÇTA GÖRÜNÜYOR
 *
 * Kullanıcı kotasını harcayan bir işlem yaptı; ne kadar harcadığını sonucun yanında
 * görmüyorsa bunu ancak abonelik ekranına gidip fark eder. Başarısız analizde de sayı var
 * ve SIFIR: iade edildiğini söylemenin en kısa yolu.
 *
 * ### ⚠ ÜRETİM ↔ KAYIT AYRIMI (dilim 2 kök nedeni 2)
 *
 * `POST .../ai-analyses` bir TASLAK üretir — sunucu kredi tüketir ama satır kalıcı
 * listeye GİRMEZ (`saved: false`). Bu ekran taslağı `analizEt.data`'dan gösterir, kalıcı
 * `liste` sorgusundan DEĞİL: liste zaten yalnız kaydedilenleri (+ başarısız denemeleri)
 * döndürüyor, taslak orada yok. Kullanıcı "Kaydet"e basmadan sayfadan ayrılırsa taslak
 * KAYBOLMAZ (denetim/maliyet izi kalıcı — KARAR 04) ama kalıcı geçmişte hiç görünmez;
 * "Vazgeç" bu yüzden bir API çağrısı değil, yalnız yerel görünümü temizliyor.
 */
export function ContractAiAnalysisSection({
  contractId,
  arsivde = false,
  kayitYazma = true,
}: {
  contractId: string;
  /**
   * ⚠ Arşivdeki sözleşmede analiz BAŞLATILMIYOR: arşivleme sözleşmeyi gündemden
   * çıkarmak ve orada kredi harcamak, kullanıcının parasını kapalı bir dosyaya yatırmak
   * olurdu. Geçmiş analizler KALIYOR — arşivleme onları silmiyor.
   */
  arsivde?: boolean;
  /** Görüntüleyenin bu kayıttaki yazma erişimi (CLOS-888). */
  kayitYazma?: boolean;
}) {
  const { activeTenantId, can } = useAuth();
  const tenantId = activeTenantId!;
  const queryClient = useQueryClient();
  const toast = useToast();
  const { preferences } = usePreferences();
  const [acikId, setAcikId] = useState<string | null>(null);
  /**
   * ⚠ TASLAK YEREL DURUMDA — sunucu sorgusunda DEĞİL. `saved: false` bir satır kalıcı
   * listeye hiç girmiyor (bkz. üstteki dosya notu); önizleme adımının verisi bu.
   */
  const [taslak, setTaslak] = useState<ContractAiAnalysisSummary | null>(null);

  const anahtar = tenantKey(tenantId, "contract", contractId, "ai-analizler");
  const analizler = useQuery({
    queryKey: anahtar,
    /**
     * ⚠ Sorgu yalnız YETKİ VARSA koşuyor. Yetkisiz kullanıcıda çalışsaydı ekran boşuna
     * 403 üretir ve hata sunumu "bir şeyler ters gitti" derdi — oysa ortada arıza yok.
     */
    enabled: can("contract.ai_analyze"),
    queryFn: async () =>
      (
        await api<ContractAiAnalysisSummary[]>(`/api/contracts/${contractId}/ai-analyses`, {
          tenantId,
        })
      ).data,
  });

  /** ⚠ Anahtar NİYETE bağlı: çift tıklama aynı anahtarı taşır (BUG-004). */
  const idempotency = useIdempotencyKey();
  const analizEt = useMutation({
    mutationFn: async () =>
      (
        await api<ContractAiAnalysisSummary>(`/api/contracts/${contractId}/ai-analyses`, {
          method: "POST",
          tenantId,
          body: JSON.stringify({}),
          /**
           * ⚠ İDEMPOTENCY ANAHTARI: ağ hatasından sonra TEKRARLANAN (aynı) istek ikinci bir
           * analiz ve ikinci bir kredi üretmemeli — sunucu içerik bazlı bir dedup ARTIK
           * yapmıyor (küçük ürün kararı, ACIK-KARAR 1'in kapanışı: "Yeniden üret" her
           * zaman yeni bir taslak/kredi üretir). Bu yüzden istemci her TIKLAMADA yeni bir
           * anahtar üretiyor (`crypto.randomUUID()` her çağrıda taze) — yalnız gerçek bir
           * ağ tekrar denemesi aynı anahtarı taşır ve sunucuda önbellekten karşılanır.
           */
          headers: { "idempotency-key": idempotency.uret() },
        })
      ).data,
    onSuccess: async (analiz) => {
      /** ⚠ Başarıdan sonra YENİ niyet yeni anahtar alıyor; yoksa kullanıcının bilinçli
          ikinci işlemi replay sayılıp sessizce ilk yanıta düşerdi (BUG-004). */
      idempotency.yenile();
      /**
       * ⚠ ABONELİK SAYACI DA TAZELENİYOR: analiz kredi harcadı ve "AI kredisi" satırı
       * eski sayıda kalsaydı kullanıcı biraz önce harcadığı krediyi hâlâ elinde sanırdı.
       */
      await queryClient.invalidateQueries({
        queryKey: tenantKey(tenantId, "subscription-overview"),
      });
      if (analiz.status === "failed") {
        /** ⚠ Başarısız deneme kaydedilmeden de kalıcı geçmişte görünür (AI-AT-02). */
        await queryClient.invalidateQueries({ queryKey: anahtar });
        setTaslak(null);
        toast.error(
          analiz.errorCode
            ? CONTRACT_AI_ANALYSIS_ERROR_MESSAGES[analiz.errorCode]
            : "AI analizi başarısız oldu",
        );
        return;
      }
      setTaslak(analiz);
      setAcikId(null);
      toast.success("AI analizi hazır — incele ve kaydet");
    },
    /**
     * ⚠ Mesaj SUNUCUDAN: kredi hakkı yok, motor yapılandırılmamış ve sağlayıcı düştü —
     * üçü ayrı cümle ve üçü de şemada tanımlı. Burada tek bir "analiz başarısız" cümlesi
     * yazsaydık kullanıcı ne yapması gerektiğini asla öğrenemezdi.
     */
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : "AI analizi çalıştırılamadı"),
  });

  /**
   * KAYDET — ayrı, açık komut (dilim 2 kök nedeni 2). Üretimle AYNI uç DEĞİL: bu çağrı
   * kredi harcamıyor, yalnız taslağı kalıcı geçmişe taşıyor.
   */
  const kaydet = useMutation({
    mutationFn: async (analysisId: string) =>
      (
        await api<ContractAiAnalysisSummary>(
          `/api/contracts/${contractId}/ai-analyses/${analysisId}/save`,
          { method: "POST", tenantId, body: JSON.stringify({}) },
        )
      ).data,
    onSuccess: async (analiz) => {
      await queryClient.invalidateQueries({ queryKey: anahtar });
      setTaslak(null);
      setAcikId(analiz.id);
      toast.success("Analiz kaydedildi");
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : "Analiz kaydedilemedi"),
  });

  /** ⚠ Yetkisi olmayan kullanıcıda BÖLÜM HİÇ ÇİZİLMİYOR (inceleme bölümüyle aynı desen). */
  if (!can("contract.ai_analyze")) return null;

  const liste = analizler.data ?? [];
  const kalici = liste.find((a) => a.id === acikId) ?? liste.find((a) => a.status === "completed");

  return (
    <Card>
      <div className="mb-3 flex items-center gap-2">
        <h2 className="mr-auto text-base font-semibold text-fg-heading">AI Analizi</h2>
        {!arsivde && kayitYazma ? (
          <Button
            type="button"
            size="sm"
            variant="secondary"
            disabled={analizEt.isPending}
            onClick={() => analizEt.mutate()}
          >
            {analizEt.isPending ? "Analiz ediliyor…" : taslak ? "Yeniden üret" : "Analiz et"}
          </Button>
        ) : null}
      </div>

      {analizler.isPending ? (
        <Skeleton lines={3} />
      ) : analizler.error ? (
        <StateView
          state={resolveErrorState(analizler.error, {
            idempotent: true,
            overrides: {
              error: {
                title: "AI analizleri yüklenemedi",
                description: "Bölüm şu an getirilemedi.",
              },
            },
          })}
          onRetry={() => void analizler.refetch()}
        />
      ) : liste.length === 0 && !taslak ? (
        <p className="text-sm text-fg-muted">Bu sözleşme için henüz AI analizi çalıştırılmadı.</p>
      ) : (
        <div className="flex flex-col gap-4">
          {taslak ? (
            <div className="flex flex-col gap-3">
              <SonucKarti analiz={taslak} locale={preferences.locale} />
              {/*
                ⚠ TASLAK EYLEMLERİ (kriter 9): kaydetmeden inceleme zaten burada — kart
                zaten çizildi. "Yeniden üret" üstteki düğme; "Vazgeç" bir API çağrısı
                değil, yalnız yerel görünümü temizliyor (KARAR 04: tüketim zaten kesin).
              */}
              {kayitYazma ? (
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    size="sm"
                    disabled={kaydet.isPending}
                    onClick={() => kaydet.mutate(taslak.id)}
                  >
                    {kaydet.isPending ? "Kaydediliyor…" : "Kaydet"}
                  </Button>
                  <Button type="button" size="sm" variant="ghost" onClick={() => setTaslak(null)}>
                    Vazgeç
                  </Button>
                </div>
              ) : null}
            </div>
          ) : kalici?.status === "completed" && kalici.output ? (
            <SonucKarti analiz={kalici} locale={preferences.locale} />
          ) : null}

          {liste.length > 0 ? (
            <div className="flex flex-col gap-2">
              <h3 className="text-sm font-medium text-fg-strong">Geçmiş analizler</h3>
              {liste.map((analiz) => (
                <Button
                  key={analiz.id}
                  variant="secondary"
                  data-testid={`ai-analiz-${analiz.id}`}
                  onClick={() => {
                    setTaslak(null);
                    setAcikId(analiz.id);
                  }}
                  className="h-auto w-full flex-wrap justify-start gap-2 p-3 text-left font-normal"
                >
                  <Badge tone={analiz.status === "completed" ? "success" : "danger"}>
                    {CONTRACT_AI_ANALYSIS_STATUS_NAMES[analiz.status]}
                  </Badge>
                  <span className="text-sm text-fg">
                    {formatDate(analiz.createdAt, preferences.locale)} · {analiz.createdByName}
                  </span>
                  {/*
                    ⚠ DÜŞEN KREDİ SATIRDA. Başarısızda 0 ve bu bir bilgi: iade edildi.
                  */}
                  <span className="ml-auto text-sm tabular-nums text-fg-muted">
                    {analiz.credits} kredi
                  </span>
                  {analiz.stale ? (
                    <span className="text-xs text-fg-muted">Güncel versiyon değil</span>
                  ) : null}
                  {analiz.errorCode ? (
                    <p className="w-full text-sm text-danger">
                      {CONTRACT_AI_ANALYSIS_ERROR_MESSAGES[analiz.errorCode]}
                    </p>
                  ) : null}
                </Button>
              ))}
            </div>
          ) : null}
        </div>
      )}
    </Card>
  );
}

/**
 * Analiz sonucu.
 *
 * ⚠ ETİKET KARTIN EN ÜSTÜNDE, sonucun altında değil: kullanıcı çıktının ne olduğunu
 * okumadan ÖNCE statüsünü bilmeli. Altına koysaydık, uzun bir analizde etiket ekranın
 * dışında kalır ve pratikte hiç görünmezdi.
 */
function SonucKarti({ analiz, locale }: { analiz: ContractAiAnalysisSummary; locale: string }) {
  const cikti = analiz.output!;
  return (
    <div
      data-testid="ai-analiz-sonuc"
      className="flex flex-col gap-4 rounded-md border border-border-default p-4"
    >
      <AiAdvisoryNotice
        meta={
          <>
            {formatDate(analiz.createdAt, locale)} · {analiz.credits} kredi
          </>
        }
      />

      <Bolum baslik="Özet">
        <p className="text-sm text-fg">{cikti.summary}</p>
      </Bolum>

      <Bolum baslik="Taraflar" bos={cikti.parties.length === 0}>
        <ul className="flex flex-col gap-1">
          {cikti.parties.map((taraf, i) => (
            <li key={`${taraf.name}-${i}`} className="text-sm text-fg">
              <span className="font-medium">{taraf.name}</span>
              {taraf.role ? <span className="text-fg-muted"> — {taraf.role}</span> : null}
            </li>
          ))}
        </ul>
      </Bolum>

      <Bolum baslik="Temel yükümlülükler" bos={cikti.obligations.length === 0}>
        <ul className="flex flex-col gap-2">
          {cikti.obligations.map((y, i) => (
            <li key={i} className="text-sm text-fg">
              {y.party ? <span className="font-medium">{y.party}: </span> : null}
              {y.description}
              <AiSourceReference source={y.source} confidence={y.confidence} />
            </li>
          ))}
        </ul>
      </Bolum>

      <Bolum baslik="Kritik tarihler" bos={cikti.criticalDates.length === 0}>
        <ul className="flex flex-col gap-2">
          {cikti.criticalDates.map((t, i) => (
            <li key={i} className="text-sm text-fg">
              <span className="font-medium">{t.label}: </span>
              {/*
                ⚠ Tarih METİNDE YAZDIĞI GİBİ gösteriliyor, biçimlendirilmiyor: sözleşmelerin
                çoğu takvim tarihi değil KOŞUL yazıyor ("imzadan 30 gün sonra"). Biçimlendirmeye
                zorlasaydık, koşul cümlesi ya kaybolur ya uydurma bir tarihe dönerdi.
              */}
              {t.date}
              {t.note ? <span className="text-fg-muted"> — {t.note}</span> : null}
              <AiSourceReference source={t.source} confidence={t.confidence} />
            </li>
          ))}
        </ul>
      </Bolum>

      <Bolum baslik="Dikkat çeken maddeler" bos={cikti.notableClauses.length === 0}>
        <ul className="flex flex-col gap-2">
          {cikti.notableClauses.map((m, i) => (
            <li key={i} className="text-sm text-fg">
              <span className="font-medium">{m.title}: </span>
              {m.concern}
              <AiSourceReference source={m.source} confidence={m.confidence} />
            </li>
          ))}
        </ul>
      </Bolum>
    </div>
  );
}

/**
 * Sonuç kartının alt başlığı.
 *
 * ⚠ Boş bölüm "—" ile ÇİZİLİYOR, gizlenmiyor: model o başlıkta bir şey bulamadıysa bu bir
 * bilgi. Gizleseydik kullanıcı analizin o başlığa hiç bakmadığını sanardı.
 */
function Bolum({
  baslik,
  bos = false,
  children,
}: {
  baslik: string;
  bos?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1">
      <h4 className="text-sm font-medium text-fg-strong">{baslik}</h4>
      {bos ? <p className="text-sm text-fg-muted">Metinde bulunamadı.</p> : children}
    </div>
  );
}
